import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'utils/axios';

// MUI imports
import { Alert, Autocomplete, Box, LinearProgress, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

// Project imports
import { useDispatch } from 'store';
import AssessmentsContext from 'contexts/AssessmentsContext';
import MainCard from 'ui-component/cards/MainCard';
import { showErrorAlert } from 'utils/global-functions';
import GroupCard from './GroupCard';
import { PHONE_TAP_TARGET, sortGrades, termNotStarted, termStartLabel } from './constants';

const uniqueSorted = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));

// Filter options keep the id with the name, so the export can filter by id on the server
const uniqueById = (rows, idKey, nameKey) => {
    const byId = {};
    rows.forEach((r) => {
        if (r[idKey] && r[nameKey]) byId[r[idKey]] = { id: r[idKey], label: r[nameKey] };
    });
    return Object.values(byId).sort((a, b) => a.label.localeCompare(b.label));
};

const EXPORT_FAILED = 'The export could not be downloaded. Please try again.';

// A failed download arrives as a Blob, so the server's message has to be read out of it. Only a plain-text
// message (all of ours) or a ProblemDetails title is shown; anything else (an HTML page, a stack trace) gets the general message.
const downloadErrorMessage = async (ex) => {
    if (!(ex instanceof Blob)) return EXPORT_FAILED;
    try {
        const text = (await ex.text()).trim();
        if (ex.type.startsWith('text/plain')) return text || EXPORT_FAILED;
        if (ex.type.includes('json')) {
            // Only the framework's model-binding 400s are JSON: show the title, else the first error
            const problem = JSON.parse(text);
            const firstError = problem.errors ? Object.values(problem.errors).flat()[0] : null;
            return problem.title || firstError || EXPORT_FAILED;
        }
    } catch {
        // Unreadable body: fall through to the general message
    }
    return EXPORT_FAILED;
};

export default function AssessmentsPage() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    // False only when none of the user's groups is at a school with assessments on (the same check as the menu)
    const { assessmentsEnabled, schoolsOff } = useContext(AssessmentsContext);
    // The term picked here is kept in the address, so coming back from Goals (or the browser's Back) reopens it
    const termFromUrl = new URLSearchParams(location.search).get('termId');

    const [terms, setTerms] = useState([]);
    const [termId, setTermId] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [school, setSchool] = useState(null);
    const [grade, setGrade] = useState(null);
    const [provider, setProvider] = useState(null);

    useEffect(() => {
        async function getTerms() {
            try {
                const { data } = await axios.get('/assessments/terms');
                setTerms(data);
                const current = data.find((t) => t.id === termFromUrl) ?? data.find((t) => t.isCurrent) ?? data[data.length - 1];
                if (current) {
                    setTermId(current.id);
                } else {
                    setLoading(false);
                }
            } catch (ex) {
                showErrorAlert(dispatch, ex);
                setLoading(false);
            }
        }

        getTerms();
    }, []);

    useEffect(() => {
        async function getList() {
            if (!termId) return;
            setLoading(true);
            try {
                const { data } = await axios.get('/assessments/list', { params: { termId } });
                setRows(data);
            } catch (ex) {
                showErrorAlert(dispatch, ex);
            }
            setLoading(false);
        }

        getList();
    }, [termId]);

    const pickTerm = (id) => {
        setTermId(id);
        navigate(`/assessments?termId=${id}`, { replace: true });
    };

    const term = terms.find((t) => t.id === termId);
    // Before the term's first day its rows are shown locked, so nobody types a value the server will refuse
    const notStarted = termNotStarted(term);
    // Every one of the user's assessment groups is at a school that's Off, so the page only says so, with no term alerts
    const assessmentsOff = schoolsOff && rows.length === 0;

    const schoolOptions = useMemo(() => uniqueById(rows, 'schoolId', 'schoolName'), [rows]);
    const gradeOptions = useMemo(() => sortGrades(uniqueSorted(rows.map((r) => r.grade))), [rows]);
    const providerOptions = useMemo(() => uniqueById(rows, 'providerId', 'providerName'), [rows]);

    const filtered = rows.filter(
        (r) => (!school || r.schoolId === school.id) && (!grade || r.grade === grade) && (!provider || r.providerId === provider.id)
    );

    // Which earlier term this term's values start from. Look-backs skip terms with nothing saved, so in Term 3 it can be
    // Term 1. Named when every student's comes from the same term.
    const sourceTerms = [...new Set(rows.map((r) => r.previousTerm).filter(Boolean))];
    let startsFrom = '';
    if (sourceTerms.length === 1) {
        startsFrom = `starts from each student's Term ${sourceTerms[0]} type and goals.`;
    } else if (sourceTerms.length > 1) {
        startsFrom = "starts from each student's type and goals from their latest earlier term.";
    }

    // One student in two groups for the subject counts once
    const blockedRows = filtered.filter((r) => r.blocked);
    const blockedCount = new Set(blockedRows.map((r) => `${r.studentId}-${r.subject}`)).size;
    // The banner names the day count when every flagged student's school uses the same one
    const blockedDays = [...new Set(blockedRows.map((r) => r.blockAfterDays))];
    const blockedFor = blockedDays.length === 1 ? `${blockedDays[0]}+ days` : "past their school's deadline";

    // Downloads this term's saved scores and goals for the current filters
    const onExport = async () => {
        setExporting(true);
        try {
            const { data } = await axios.get('/assessments/export', {
                params: { termId, schoolId: school?.id, grade, providerId: provider?.id },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Assessments Term ${term?.term ?? ''}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (ex) {
            showErrorAlert(dispatch, await downloadErrorMessage(ex));
        } finally {
            setExporting(false);
        }
    };

    const groups = useMemo(() => {
        const list = [];
        const index = {};
        filtered.forEach((r) => {
            if (index[r.groupId] === undefined) {
                index[r.groupId] = list.length;
                list.push({ ...r, rows: [] });
            }
            list[index[r.groupId]].rows.push(r);
        });
        return list.sort((a, b) => a.groupName.localeCompare(b.groupName));
    }, [filtered]);

    // Rows saving right now; the term can't change mid-save, or the answer would land on the other term's rows
    const [savesInFlight, setSavesInFlight] = useState(0);
    const onSavingChange = (started) => setSavesInFlight((n) => Math.max(0, n + (started ? 1 : -1)));

    // The assessment belongs to the student and subject, so update every row for that pair (e.g. the same student in two groups).
    // A score save never touches goal ratings, so the row keeps its own counts and takes only the type, score and block. Each
    // group's row takes its own block flag (a student who left one group early isn't flagged there); a reply without them
    // gives one flag for all.
    const onSaved = (row, saved) => {
        const update = {
            assessmentType: saved.assessmentType,
            score: saved.score,
            typeRequired: saved.typeRequired
        };
        setRows((prev) =>
            prev.map((r) =>
                r.studentId === row.studentId && r.subject === row.subject
                    ? { ...r, ...update, blocked: saved.blockedByGroup?.[r.groupId] ?? saved.blocked }
                    : r
            )
        );
    };

    // Someone who reaches this page by its address without the menu item is told why it's empty
    let emptyMessage = 'There are no students in your groups for this term.';
    if (school || grade || provider) emptyMessage = 'No students match these filters.';
    else if (assessmentsOff) emptyMessage = "Assessments aren't turned on for your school.";
    else if (!assessmentsEnabled) emptyMessage = 'There are no assessments for your groups.';

    return (
        <MainCard
            contentSX={{ px: { xs: 1.5, sm: 3 } }}
            title={
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    <Box>Assessments</Box>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <LoadingButton
                            variant="outlined"
                            size="small"
                            startIcon={<FileDownloadOutlinedIcon />}
                            loading={exporting}
                            loadingPosition="start"
                            sx={PHONE_TAP_TARGET}
                            onClick={onExport}
                            // Not while a row is saving, so the file has the score that was just typed
                            disabled={loading || !termId || filtered.length === 0 || savesInFlight > 0}
                        >
                            Export to Excel
                        </LoadingButton>
                        {terms.length > 0 && (
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                color="secondary"
                                value={termId}
                                disabled={savesInFlight > 0}
                                onChange={(e, v) => v && pickTerm(v)}
                                sx={{
                                    gap: 1,
                                    '& .MuiToggleButton-root': {
                                        border: 0,
                                        borderRadius: '8px !important',
                                        px: 2,
                                        py: 0.5,
                                        ...PHONE_TAP_TARGET,
                                        textTransform: 'none',
                                        fontWeight: 400,
                                        color: 'grey.600',
                                        '&:hover': { backgroundColor: 'grey.100' }
                                    },
                                    '& .MuiToggleButton-root.Mui-selected': {
                                        fontWeight: 500,
                                        color: 'secondary.dark',
                                        backgroundColor: 'secondary.light',
                                        '&:hover': { backgroundColor: 'secondary.light' }
                                    }
                                }}
                            >
                                {terms.map((t) => (
                                    <ToggleButton key={t.id} value={t.id}>
                                        Term {t.term}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        )}
                    </Stack>
                </Box>
            }
        >
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                <Autocomplete
                    sx={{ width: { xs: '100%', sm: 240 } }}
                    size="small"
                    options={schoolOptions}
                    value={school}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(e, v) => setSchool(v)}
                    renderInput={(params) => <TextField {...params} label="School" />}
                />
                <Autocomplete
                    sx={{ width: { xs: '100%', sm: 140 } }}
                    size="small"
                    options={gradeOptions}
                    value={grade}
                    onChange={(e, v) => setGrade(v)}
                    renderInput={(params) => <TextField {...params} label="Grade" />}
                />
                <Autocomplete
                    sx={{ width: { xs: '100%', sm: 240 } }}
                    size="small"
                    options={providerOptions}
                    value={provider}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(e, v) => setProvider(v)}
                    renderInput={(params) => <TextField {...params} label="Provider" />}
                />
            </Stack>

            {notStarted && !assessmentsOff && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Term {term.term} starts on {termStartLabel(term)}. You can enter scores from that day.
                </Alert>
            )}

            {!loading && !notStarted && !assessmentsOff && term && term.term > 1 && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Term {term.term} {startsFrom || 'has begun.'} Each student needs a new score for this term.
                </Alert>
            )}

            {!loading && blockedCount > 0 && (
                <Alert severity="error" icon={<WarningAmberRoundedIcon fontSize="inherit" />} sx={{ mb: 3 }}>
                    {blockedCount} student{blockedCount > 1 ? 's have' : ' has'} gone {blockedFor} without an assessment score entered.
                    Billing is blocked for sessions with {blockedCount > 1 ? 'these students' : 'this student'} until it&apos;s entered.
                </Alert>
            )}

            {loading && <LinearProgress />}

            {!loading && terms.length === 0 && (
                <Typography align="center">No assessment terms have been set up for this school year.</Typography>
            )}

            {!loading && terms.length > 0 && groups.length === 0 && (
                <Typography align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {emptyMessage}
                </Typography>
            )}

            {!loading &&
                groups.map((g) => <GroupCard key={g.groupId} group={g} term={term} onSaved={onSaved} onSavingChange={onSavingChange} />)}
        </MainCard>
    );
}
